import React, { useRef, useEffect, useState } from 'react';
import { useTourRegistry } from '../context/TourContext';
import type { StepHandle } from '../context/TourContext';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { useElementObserver } from './useElementObserver';
import { addError } from '@shared/utils/logger';

/**
 * Hook to register a tour step with the TourProvider.
 *
 * @performance - Optimized with:
 * - Extracted observer pattern (useElementObserver) for reusability
 * - Only re-registers when tooltip actually changes
 * - Efficient change detection to avoid unnecessary work
 */
export const useTourStepRegistration = (stepName: string, tourName: string, tooltip: React.ReactNode) => {
  const context = useTourRegistry();
  const tooltipRef = useRef(tooltip);
  const handleRef = useRef<StepHandle | null>(null);
  const prevTooltipRef = useRef<React.ReactNode>(tooltip);
  const [element, setElement] = useState<HTMLElement | null>(null);
  // Store latest values for cleanup
  const contextRef = useRef(context);
  const stepNameRef = useRef(stepName);
  const tourNameRef = useRef(tourName);

  // Use extracted element observer hook
  const observe = useElementObserver(element);

  // Update refs when props change (but don't trigger re-registration)
  tooltipRef.current = tooltip;
  contextRef.current = context;
  stepNameRef.current = stepName;
  tourNameRef.current = tourName;

  // Only re-register if values actually changed to avoid unnecessary work
  useEffect(() => {
    if (!handleRef.current || !context || !element) {
      return;
    }

    // Log telemetry error if step belongs to different tour (indicates multiple tours running)
    if (context.tourName !== tourName) {
      addError(new Error('Tour step registration mismatch'), {
        stepName,
        stepTourName: tourName,
        activeTourName: context.tourName,
        context: 'useTourStepRegistration',
        message: `TourStep "${stepName}" attempted to re-register with tour "${tourName}" but active tour is "${context.tourName}". Multiple tours may be running simultaneously.`,
      });
      return;
    }

    const node = element;

    // Check if tooltip changed
    const tooltipChanged = prevTooltipRef.current !== tooltip;

    if (!tooltipChanged) {
      return;
    }

    // Update previous values
    prevTooltipRef.current = tooltip;

    // Create new handle with updated values
    const updatedHandle: StepHandle = {
      stepName,
      tourName,
      node,
      getRect: () => node.getBoundingClientRect(),
      observe,
      tooltip: tooltipRef.current,
      placement: 'auto',
      borderRadius: 8,
    };
    handleRef.current = updatedHandle;
    context.registerStep(stepName, tourName, updatedHandle);
  }, [tooltip, context, stepName, tourName, observe, element]);

  const elementRef = useRef<HTMLElement | null>(null);

  const callbackRef = useEventCallback((node: HTMLElement | null) => {
    if (!context) return;

    // Only register if this step belongs to the current tour
    if (context.tourName !== tourName) {
      // Log telemetry error: step trying to register with wrong tour (indicates multiple tours running)
      addError(new Error('Tour step registration mismatch'), {
        stepName,
        stepTourName: tourName,
        activeTourName: context.tourName,
        message: `TourStep "${stepName}" attempted to register with tour "${tourName}" but active tour is "${context.tourName}"`,
      });
      return;
    }

    // Only update state if element actually changed to prevent infinite loops
    const elementChanged = elementRef.current !== node;
    if (elementChanged) {
      elementRef.current = node;
      setElement(node);
    }

    if (node) {
      // Only re-register if element changed or handle doesn't exist
      if (elementChanged || !handleRef.current) {
        const handle: StepHandle = {
          stepName,
          tourName,
          node,
          getRect: () => node.getBoundingClientRect(),
          observe,
          tooltip: tooltipRef.current,
          placement: 'auto',
          borderRadius: 8,
        };

        handleRef.current = handle;
        context.registerStep(stepName, tourName, handle);
      }
    } else {
      if (handleRef.current) {
        handleRef.current = null;
        if (elementRef.current !== null) {
          elementRef.current = null;
          setElement(null);
        }
        context.unregisterStep(stepName, tourName);
      }
    }
  });

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      if (handleRef.current && contextRef.current) {
        handleRef.current = null;
        setElement(null);
        contextRef.current.unregisterStep(stepNameRef.current, tourNameRef.current);
      }
    };
  }, []);

  return callbackRef;
};
